import { useSiteTitle, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import { useImageUrl } from '@dustin-web/microsite-components/src/hooks/use-image-url'
import Image from '@dustin-web/microsite-components/src/image'
import { useGraphql } from '@dustin-web/microsite-data/src/hooks'
import { log } from '@dustin-web/microsite-config/src/logger'
import { withMicrositeProps } from '@dustin-web/microsite-nextjs/src/with-server-data/with-microsite-props'
import { withMicrositeWrapper } from '@dustin-web/microsite-nextjs/src/with-server-data/with-microsite-wrapper'
import Head from 'next/head'
import AccessoryGuideBreadcrumbs from '@productsite/src/components/accessory-guides/accessory-guide-breadcrumbs'
import {
  GetAccessoryGuidesQuery,
  GetAccessoryGuidesQueryVariables,
} from '@productsite/src/graphql/generated/graphql-types'
import { GetAccessoryGuides } from '@productsite/src/graphql/get-accessory-guides.graphql'
import Link from 'next/link'

type GuideBoxProps = {
  url: string
  imageUrl: string
  text: string
  alt: string
}

const GuideBox = ({ url, imageUrl, text, alt }: GuideBoxProps) => {
  return (
    <div className="col-12 col-sm-6 col-md-4 u-mb-16">
      <Link
        prefetch={false}
        className="c-box d-flex flex-column align-items-center u-decoration-none"
        href={url}>
        {imageUrl && <Image src={imageUrl} alt={alt} width={160} height={80} className="u-pb-8" />}
        <span>{text}</span>
      </Link>
    </div>
  )
}

const AccessoryGuideListPage = () => {
  const { data } = useGraphql<GetAccessoryGuidesQuery, GetAccessoryGuidesQueryVariables>(
    GetAccessoryGuides,
    {}
  )
  const { getSiteTitle } = useSiteTitle()
  const { getImageUrl } = useImageUrl()
  const { translate } = useTranslations()

  const categories = data?.accessoryGuides ?? []

  return (
    <>
      <Head>
        <title>{getSiteTitle(translate('AccessoryGuides_Title'))}</title>
      </Head>
      <div className="container">
        <AccessoryGuideBreadcrumbs />
      </div>
      <div className="t-light-blue u-py-40 u-mb-32">
        <div className="container">
          <h1 className="u-mb-0">{translate('AccessoryGuides_Header')}</h1>
        </div>
      </div>
      <div className="container u-mb-32">
        <div className="row no-gutters">
          <div className="col-12 col-sm-12 col-md-9 col-xl-7">
            <p className="u-mb-32">{translate('AccessoryGuides_MainText')}</p>
            <h2 className="h3 u-fw-bold">{translate('AccessoryGuides_SubHeader')}</h2>
            <p>{translate('AccessoryGuides_SubText')}</p>
          </div>
        </div>
      </div>
      <div className="container">
        <div className="row no-gutters sm-gutters u-mb-96 justify-content-center">
          {categories.map((category: any, i: number) => (
            <GuideBox
              key={category.name + i}
              url={`/accessories/guides/${category.pathSlug}`}
              imageUrl={getImageUrl({
                imageFilename: category.mostPopularProduct?.searchResultProduct?.primaryImageId,
                xSize: 320,
                ySize: 160,
              })}
              alt=""
              text={category.seoLongTitleName ?? category.name}
            />
          ))}
        </div>
      </div>
    </>
  )
}

export const getServerSideProps = withMicrositeProps(async ctx => {
  const { micrositeProps } = ctx
  const { requestContext, fetchGraphql } = micrositeProps

  const props = {
    fallback: {},
  }

  try {
    const [key, data] = await fetchGraphql<
      GetAccessoryGuidesQuery,
      GetAccessoryGuidesQueryVariables
    >(GetAccessoryGuides, {})

    props.fallback = { [key]: data }
  } catch (err) {
    log.warn({ traceIdentifier: requestContext.correlationId, err }, `Error accessory guides page`)
  }

  return { props }
})

export default withMicrositeWrapper(AccessoryGuideListPage)
